import CompilerSelectControlledComponent from 'components/v2/epd-compiler-inputs/CompilerSelectControlledComponent';
import WizardTextAreaComponent from 'components/v2/epd-wizard-inputs/WizardTextArea';
import WizardTextInputComponent from 'components/v2/epd-wizard-inputs/WizardTextInput';
import { HelpBox } from 'components/v2/help-boxes';
import TooltipErrorIcon from 'components/v2/icons/TooltipErrorIcon';
import { ImagesUpload } from 'components/v2/images-upload';
import SmartLabel from 'components/v2/labels/SmartLabel';
import { useState } from 'react';
import { ActionState } from 'services/EpdClient';
import {
  useAddProductInformationFileMutation,
  useArrangeProductInformationFilesMutation,
  useRemoveProductInformationFileMutation,
} from 'services/api/mutations';
import { addEpdFile } from 'services/api/mutations';
import { useDictionaryValues, useGetEpdPCRInformation } from 'services/api/queries';
import {
  ExpandablePanelHeaderName,
  ExpandablePanelModule,
  HeaderCaptionSemiBold,
  HelpBoxContainer,
} from 'styles/v2/Styles.styled';
import { EPDProductInformationModel, ProductStatusEnum } from 'types/types';

import { YES_NO } from '../../constants';
import ToggleButton from '../../panels/toggleable/ToggleButton';
import { getOptionsFromDictionary } from 'util/utils';
import { getValidationIssuesCount } from 'util/compilerValidation';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import usePcrV2 from '../../hooks/usePcrV2';

const ProductDetailsSection: React.FunctionComponent<{
  product?: EPDProductInformationModel;
  epdId: string;
  epdVersionId: string;
  onChange: any;
  errors?: any;
}> = ({ product, epdId, epdVersionId, onChange, errors }) => {
  const pcrInformation = useGetEpdPCRInformation(epdVersionId).data;
  const addProductInformationFileMutation = useAddProductInformationFileMutation(epdVersionId);
  const removeProductInformationFileMutation = useRemoveProductInformationFileMutation(epdVersionId);
  const arrangeProductInformationFilesMutation = useArrangeProductInformationFilesMutation(epdVersionId);
  const dictionariesValues = useDictionaryValues().data;
  const isPCRV2Selected = usePcrV2();
  const productDetailsErrors = errors?.productDeclaration;
  const isTheProjectInThePlanningOrDesignPhaseLookup = [
    { value: false, label: YES_NO.NO },
    { value: true, label: YES_NO.YES },
  ];
  const [isTheProjectInThePlanningOrDesignPhaseFlag, setIsTheProjectInThePlanningOrDesignPhaseFlag] = useState<
    boolean | undefined | null
  >(product?.isTheProjectInThePlanningOrDesignPhase);
  const productStatusesOptions = getOptionsFromDictionary(dictionariesValues, EPD_DICTIONARY_CODES.PRODUCT_STATUSES);
  const selectedProductStatus = productStatusesOptions.find(
    (x) => product?.productStatus && x.value === Object.keys(ProductStatusEnum).indexOf(product?.productStatus).toString()
  );
  const state: Record<string, ActionState> = {
    isTheProjectInThePlanningOrDesignPhase: pcrInformation?.cpcr?.isCPCR22OrCPCR23Selected
      ? ActionState.Enabled
      : ActionState.Hidden,
  };

  const handleUploadProductImage = async (propertyName: string, file: File) => {
    await addEpdFile(
      { epdId, propertyName, file, epdProductInformationId: product?.id, documentTypeId: null, documentLanguageId: null },
      addProductInformationFileMutation
    );
  };

  const onRemoveProductDetailsImage = async (fileId: string, propertyName: string) => {
    removeProductInformationFileMutation.mutate(fileId);
  };

  const onReorderProductDetailsImages = (imgIds: string[]) => {
    if (!product?.id) {
      return;
    }

    arrangeProductInformationFilesMutation.mutate({ productId: product.id, fileIds: imgIds });
  };

  const onChangeIsTheProjectInThePlanningOrDesignPhaseFlag = (name: any, value: any) => {
    setIsTheProjectInThePlanningOrDesignPhaseFlag(value ?? undefined);
    onChange(name, value);
  };

  const headerModuleTemplate = (options: any, header: string, required?: boolean) => {
    const hasIssues = getValidationIssuesCount(productDetailsErrors) > 0;

    return (
      <div className={`${options.className} ${options.collapsed ? 'header-collapsed' : ''}`}>
        <ExpandablePanelHeaderName>
          <HeaderCaptionSemiBold>{header}</HeaderCaptionSemiBold>
          {required && hasIssues && <TooltipErrorIcon content={productDetailsErrors} />}
          <ToggleButton options={options} />
        </ExpandablePanelHeaderName>
      </div>
    );
  };

  return (
    <ExpandablePanelModule headerTemplate={(options) => headerModuleTemplate(options, 'Product details', true)} toggleable>
      <WizardTextInputComponent
        label="Product name"
        tooltip="Name of the product included in the EPD."
        onChanged={onChange}
        required
        placeholder="Unnamed"
        name="productName"
        value={product?.productName ?? ''}
        error={productDetailsErrors?.name}
      />
      {isPCRV2Selected && (
        <CompilerSelectControlledComponent
          label="Product status"
          name="productStatus"
          options={productStatusesOptions}
          value={selectedProductStatus}
          onChanged={(name: string, value: string) => onChange(name, Object.values(ProductStatusEnum)[Number(value)])}
          required
          placeholder="Select"
          error={productDetailsErrors?.productStatus}
          isClearable={false}
        />
      )}
      <CompilerSelectControlledComponent
        label="Is the project in the planning/design phase?"
        tooltip="Define is the project in the planning/design phase"
        name="isTheProjectInThePlanningOrDesignPhase"
        options={isTheProjectInThePlanningOrDesignPhaseLookup}
        value={isTheProjectInThePlanningOrDesignPhaseLookup?.find(
          (x) => x.value === isTheProjectInThePlanningOrDesignPhaseFlag
        )}
        onChanged={onChangeIsTheProjectInThePlanningOrDesignPhaseFlag}
        state={state}
        placeholder="Select"
        error={productDetailsErrors?.isTheProjectInThePlanningOrDesignPhase}
      />
      <WizardTextAreaComponent
        label="Product identification"
        required
        placeholder="Type here"
        tooltip="Product identification by an unambiguous identification of the product by standards, concessions, or other means."
        value={product?.productIdentification ?? ''}
        onChanged={onChange}
        name="productIdentification"
        error={productDetailsErrors?.productIdentification}
      />
      <WizardTextAreaComponent
        label="Product description"
        required
        placeholder="Type here"
        tooltip="Description of the product in accordance with the product classification system(s) used, and description of the technical performance of the product, including its key functionalities"
        value={product?.productDescription ?? ''}
        onChanged={onChange}
        name="productDescription"
        error={productDetailsErrors?.productDescription}
      />
      <WizardTextAreaComponent
        label="Product information from external sources"
        placeholder="Type here"
        tooltip="References to any relevant websites for more information or explanatory materials."
        value={product?.productInformationExternal ?? ''}
        onChanged={onChange}
        name="productInformationExternal"
        error={productDetailsErrors?.productInformationExternal}
      />
      <div>
        <SmartLabel
          label={'Product images shown on EPD'}
          required
          hasValue={!!product?.productDetailsImages?.length}
          tooltip="Product images shown on www.environdec.com and in the EPD."
          error={productDetailsErrors?.productInformationsToFiles}
        />
        <ImagesUpload
          name={'ProductDetailsImages'}
          images={product?.productDetailsImages}
          onUpload={handleUploadProductImage}
          onReorder={onReorderProductDetailsImages}
          onRemove={onRemoveProductDetailsImage}
        />
        <HelpBoxContainer>
          {product?.productDetailsImages && product?.productDetailsImages.length > 1 && (
            <HelpBox>
              <span>Use drag and drop to reorder the images.</span>
            </HelpBox>
          )}
          <HelpBox>
            <span>
              Upload an image with a recommended resolution of 150–300 DPI. Images will be proportionally adjusted to fit the
              page size. To see image placement in the document, use the "PDF preview" option in the upper right corner.
            </span>
          </HelpBox>
        </HelpBoxContainer>
      </div>
      <WizardTextAreaComponent
        label="Technical purpose of product"
        placeholder="Type here"
        tooltip="The application/intended use of the product."
        value={product?.technicalPurposeOfProduct ?? ''}
        onChanged={onChange}
        name="technicalPurposeOfProduct"
        error={productDetailsErrors?.technicalPurposeOfProduct}
      />
      <WizardTextAreaComponent
        label="Manufacturing or service provision description"
        placeholder="Type here"
        tooltip="Brief description of main processes of manufacturing (for EPDs of goods) or service provision (for EPDs of services)."
        value={product?.manufacturingOrServiceProvisionDescription ?? ''}
        onChanged={onChange}
        name="manufacturingOrServiceProvisionDescription"
        error={productDetailsErrors?.manufacturingOrServiceProvisionDescription}
      />
    </ExpandablePanelModule>
  );
};

export default ProductDetailsSection;
